import { Link } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Database, 
  Eye, 
  CheckCircle, 
  Server,
  RefreshCw,
  Mail,
  FileText,
  Users,
  AlertTriangle
} from 'lucide-react';
import { PublicHeader } from '@/components/marketing/PublicHeader';
import { PublicFooter } from '@/components/marketing/PublicFooter';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';

const infrastructureFeatures = [
  {
    icon: Server,
    title: 'Edge-Distributed Infrastructure',
    description: 'Global edge network with automatic failover ensures low latency and high availability worldwide.',
  },
  {
    icon: Shield,
    title: 'DDoS Protection',
    description: 'Network-level protection against distributed denial-of-service attacks with automatic mitigation.',
  },
  {
    icon: Lock,
    title: 'TLS 1.3 Encryption',
    description: 'All connections are encrypted using the latest TLS 1.3 protocol with automatic HTTPS enforcement.',
  },
  {
    icon: CheckCircle,
    title: 'SOC 2 Type II Compliant',
    description: 'Our infrastructure meets SOC 2 Type II standards for security, availability, and confidentiality.',
  },
];

const dataProtectionFeatures = [
  {
    icon: Database,
    title: 'AES-256 Encryption at Rest',
    description: 'All stored data is encrypted using AES-256, the industry standard for data protection.',
  },
  {
    icon: Users,
    title: 'Row-Level Security',
    description: 'Database-level tenant isolation ensures complete separation between organizations.',
  },
  {
    icon: Lock,
    title: 'Principle of Least Privilege',
    description: 'Access controls enforce minimal permissions required for each operation.',
  },
  {
    icon: RefreshCw,
    title: 'Point-in-Time Recovery',
    description: 'Encrypted backups with granular recovery capabilities protect against data loss.',
  },
];

const applicationSecurityFeatures = [
  {
    icon: Shield,
    title: 'Multi-Factor Authentication',
    description: 'Additional security layer available for all user accounts.',
  },
  {
    icon: Users,
    title: 'Role-Based Access Control',
    description: 'Granular permissions ensure users only access what they need.',
  },
  {
    icon: Lock,
    title: 'Secure Session Management',
    description: 'Automatic session expiration and secure token handling.',
  },
  {
    icon: CheckCircle,
    title: 'PCI-DSS Compliant Payments',
    description: 'Payment processing meets the highest security standards for card data.',
  },
];

const monitoringFeatures = [
  {
    icon: AlertTriangle,
    title: 'Real-Time Error Detection',
    description: 'Immediate alerting and response to application issues.',
  },
  {
    icon: Shield,
    title: 'Automated Security Scanning',
    description: 'Continuous vulnerability detection and policy validation.',
  },
  {
    icon: Eye,
    title: 'Performance Monitoring',
    description: 'Anomaly detection identifies unusual patterns before they become issues.',
  },
];


const securityPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Security & Trust - Radius',
  description: 'Learn about Radius security practices, compliance certifications, and data protection measures.',
  url: 'https://useradius.io/security',
};

export default function Security() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Security & Trust - Radius"
        description="Enterprise-grade security with SOC 2 compliance, AES-256 encryption, and row-level data isolation. Learn how Radius protects your procurement data."
        canonical="/security"
        structuredData={securityPageSchema}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
              <Shield className="h-4 w-4" />
              Enterprise-Grade Security
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Security & <span className="text-primary">Trust</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Built with security-first architecture to protect your procurement data. 
              Our platform meets the highest standards for compliance, encryption, and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Infrastructure Security */}
      <section className="py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold">Infrastructure Security</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade infrastructure with built-in protection at every layer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {infrastructureFeatures.map((feature) => (
              <div key={feature.title} className="p-6 rounded-lg border bg-background">
                <feature.icon className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Protection */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold">Data Protection</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your data is protected with industry-leading encryption and access controls.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dataProtectionFeatures.map((feature) => (
              <div key={feature.title} className="p-6 rounded-lg border bg-background">
                <feature.icon className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Security */}
      <section className="py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold">Application Security</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Multiple layers of authentication and authorization protect your accounts.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applicationSecurityFeatures.map((feature) => (
              <div key={feature.title} className="p-6 rounded-lg border bg-background">
                <feature.icon className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Monitoring */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold">Continuous Monitoring</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Proactive detection and response keeps your data safe around the clock.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {monitoringFeatures.map((feature) => (
              <div key={feature.title} className="p-6 rounded-lg border bg-background">
                <feature.icon className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsible Disclosure & Contact */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center space-y-8">
            <h2 className="text-3xl font-bold">Security Contact</h2>
            <p className="text-muted-foreground">
              We take security seriously. If you've discovered a vulnerability or have security concerns, 
              we encourage responsible disclosure.
            </p>
            <Button size="lg" asChild>
              <a href="mailto:security@useradius.io">
                <Mail className="mr-2 h-4 w-4" />
                security@useradius.io
              </a>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
